import { franc, francAll } from 'franc-min';
import type { TranslationDocType, TranslationLanguage } from '@ast-data/shared';
import { pairId } from '../../utils/deterministicId';

/** What a field holds of its translation — the content, not the bookkeeping. */
export type TranslationContent = Pick<
    TranslationDocType,
    | 'sourceLang'
    | 'targetLang'
    | 'sourceText'
    | 'text'
    | 'origin'
    | 'approvedBy'
    | 'approvedAt'
>;

/**
 * How a translation stands against the original as it reads NOW. `stale`
 * outranks everything: an approval was given to a text the field no longer
 * holds.
 */
export type TranslationStatus = 'machine' | 'edited' | 'approved' | 'stale';

/** One translation per (record, field) — see the translation entity. */
export function translationId(ownerId: string, field: string): string {
    return pairId(ownerId, `translation:${field}`);
}

/**
 * Whether the original has changed since it was translated. Whitespace at the
 * ends does not count: a trailing space from the phrase library is not a new
 * text.
 */
export function isStale(
    translation: Pick<TranslationContent, 'sourceText'>,
    currentText: string
): boolean {
    return translation.sourceText.trim() !== currentText.trim();
}

export function translationStatus(
    translation: TranslationContent,
    currentText: string
): TranslationStatus {
    if (isStale(translation, currentText)) {
        return 'stale';
    }
    if (translation.approvedBy !== '') {
        return 'approved';
    }
    return translation.origin === 'edited' ? 'edited' : 'machine';
}

/**
 * The text a deliverable in `language` takes for a field: the translation if
 * it is INTO that language, approved and still current — otherwise the
 * original, whatever language it is in. Unapproved machine text never goes
 * into a deliverable.
 */
export function deliverableText(
    original: string,
    translation: TranslationContent | null,
    language: TranslationLanguage
): string {
    if (
        translation &&
        translation.targetLang === language &&
        translationStatus(translation, original) === 'approved'
    ) {
        return translation.text;
    }
    return original;
}

const FRANC_CODES: Record<string, TranslationLanguage> = {
    deu: 'de',
    eng: 'en'
};

/**
 * Which of the two languages a text is written in, or null when it is too
 * short or too mixed to tell. Local and free — DeepL would bill for the
 * question. Only a suggestion: it orders the translate menu, nothing more.
 */
export function detectLanguage(text: string): TranslationLanguage | null {
    const code = franc(text, { only: ['deu', 'eng'], minLength: 12 });
    return FRANC_CODES[code] ?? null;
}

export function otherLanguage(
    language: TranslationLanguage
): TranslationLanguage {
    return language === 'de' ? 'en' : 'de';
}

/** Below this, franc's guesses are too loose to act on. */
const FOREIGN_MIN_LENGTH = 30;

/** How close German or English may come before a text counts as theirs. */
const FOREIGN_MAX_OWN_SCORE = 0.8;

/**
 * Whether a text is plainly in neither of our languages — Polish, French,
 * Romanian … — so DeepL should tell which instead of being told.
 *
 * Deliberately reluctant. Naming the source is what lets DeepL apply the
 * glossary, and franc takes short German keyword lists for French or Dutch
 * ("Dunkelbrauner Lehm" → French). So only a text long enough, whose best
 * guess is a third language with German and English both well behind, goes
 * out unnamed; anything less certain is treated as before.
 */
export function looksForeign(text: string): boolean {
    const sample = text.slice(0, 1500);
    if (sample.trim().length < FOREIGN_MIN_LENGTH) {
        return false;
    }
    const guesses = francAll(sample, { minLength: FOREIGN_MIN_LENGTH });
    const [best] = guesses[0] ?? ['und'];
    if (best === 'und' || best in FRANC_CODES) {
        return false;
    }
    return guesses.every(
        ([code, score]) =>
            !(code in FRANC_CODES) || score < FOREIGN_MAX_OWN_SCORE
    );
}

/**
 * DeepL names the source in its own codes ('de', 'en', 'fr', 'pt-BR' …); kept
 * as the bare language, lower-case. Only something that is no language code at
 * all falls back to the language the text was translated away from.
 */
export function normalizeSourceLanguage(
    detected: string,
    targetLang: TranslationLanguage
): string {
    const base = detected.toLowerCase().split('-')[0];
    if (/^[a-z]{2,3}$/.test(base)) {
        return base;
    }
    return otherLanguage(targetLang);
}
